import React from 'react';
import { HStack, Text, Textarea, Button, VStack, Portal, Dialog, CloseButton } from '@chakra-ui/react';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import moment from 'moment';
import AttachmentsList from './AttachmentsList';
import theme from '../theme';

const PhotoAttachments = ({
	formTitle,
	currentProject,
	currentPhase,
	currentPacketId,
	currentFormId,
	attachmentType,
	readOnly,
	attachments: photos,
	saveForm,
	activeLog,
	nav = true,
	navDrawerEnabled = false,
	navDrawerOpen = false,
	addAttachment,
	removeAttachment
}) => {
	const [errorDialog, setErrorDialog] = React.useState(null);
	const [comment, setComment] = React.useState('');
	const [acceptedFiles, setAcceptedFiles] = React.useState([]);

	const errorModalRef = React.useRef();

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: 'image/*',
		onDropAccepted: setAcceptedFiles
	});

	return (
		<>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={errorModalRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setErrorDialog(null);
					}
				}}
				open={errorDialog}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									Error Adding Photo Attachment
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>{errorDialog}</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={errorModalRef}
										ml={3}
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => setErrorDialog(null)}
									>
										Okay
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			{!readOnly ? (
				<VStack m="16px">
					<Text as="label" alignSelf="flex-start" fontWeight="bold">
						Add Photo Form
					</Text>
					<HStack w="100%" spacing="16px">
						<VStack
							w="225px"
							h="225px"
							alignItems="center"
							justifyContent="center"
							cursor="pointer"
							bgColor={isDragActive ? theme.colors.gray[600] : 'white'}
							border={'3px dashed ' + theme.colors.gray[600]}
							{...getRootProps()}
						>
							<input {...getInputProps()} />
							<FaCloudUploadAlt color={theme.colors.gray[900]} size="3x" />
							<Text textAlign="center">
								{acceptedFiles.length ? acceptedFiles[0].name : 'Drag image or click to choose'}
							</Text>
						</VStack>
						<VStack flex={1} h="225px" align="flex-start">
							<Text fontWeight="bold">Note</Text>
							<Textarea h="100%" value={comment} onChange={({ target }) => setComment(target.value)} />
						</VStack>
					</HStack>
					<HStack w="100%" justify="flex-end" spacing="16px">
						<Button
							disabled={!acceptedFiles.length}
							color={theme.colors.white}
							bg={theme.colors.blue[700]}
							_hover={{ backgroundColor: theme.colors.blue[800] }}
							onClick={() => {
								if (acceptedFiles.length) {
									const reader = new FileReader();
									const [file] = acceptedFiles;

									reader.onabort = () => console.error('file reading was aborted');
									reader.onerror = () => console.error('file reading has failed');
									reader.onload = () => {
										const extension = file.name.split('.').pop();
										const photo_number = photos.filter(({ type }) => type === 'photos').length + 1;

										return addAttachment({
											attachment_comment: comment,
											attachment_type: attachmentType,
											packet_id: currentPacketId,
											form_id: currentFormId,
											form_title: formTitle,
											attachment_creator:
												currentFormId === 'safety_observation'
													? ''
													: localStorage.getItem('username'),
											attachment_diver: activeLog,
											file:
												currentFormId === 'safety_observation'
													? file.name
													: currentProject +
														'-' +
														currentPhase +
														'_' +
														moment(Date.now()).format('MM-DD-YYYY') +
														'_p' +
														photo_number +
														'.' +
														extension,
											image: reader.result
										})
											.then(attachment => {
												if (saveForm) {
													saveForm(attachment, true);
												}

												setAcceptedFiles([]);
												setComment('');
											})
											.catch(error => setErrorDialog(error.message));
									};

									reader.readAsArrayBuffer(file);
								}
							}}
						>
							Save Photo
						</Button>
					</HStack>
				</VStack>
			) : null}
			<AttachmentsList
				nav={nav}
				attachments={photos}
				readOnly={readOnly}
				currentPacketId={currentPacketId}
				attachmentType={attachmentType}
				saveForm={saveForm}
				navDrawerEnabled={navDrawerEnabled}
				navDrawerOpen={navDrawerOpen}
				removeAttachment={removeAttachment}
			/>
		</>
	);
};

export default PhotoAttachments;
